import React, { FC, useEffect, useRef, useState } from 'react';
import { getLanguage } from '../../store/translations/hooks';

interface GoogleSignInButtonProps {
    onSuccess: (credential: string) => void;
    onError?: (error: any) => void;
    disabled?: boolean;
    clientId: string;
}

declare global {
    interface Window {
        google?: {
            accounts: {
                id: {
                    initialize: (config: any) => void;
                    prompt: () => void;
                    renderButton: (parent: HTMLElement, options: any) => void;
                };
            };
        };
    }
}

let gsiScriptPromise: Promise<void> | null = null;
const googleScriptSrc = 'https://accounts.google.com/gsi/client';
const googleScriptSelector = `script[src="${googleScriptSrc}"]`;
const googleScriptStatusAttribute = 'data-gsi-status';

const resolveGoogleButtonLocale = (): 'en' | 'pt' | 'fr' => {
    const appLanguage = getLanguage();
    if (appLanguage === 'fr' || appLanguage === 'en') {
        return appLanguage;
    }

    return 'pt';
};

const ensureGoogleScript = (): Promise<void> => {
    if (typeof window !== 'undefined' && window.google?.accounts?.id) {
        return Promise.resolve();
    }

    if (gsiScriptPromise) {
        return gsiScriptPromise;
    }

    const scriptPromise = new Promise<void>((resolve, reject) => {
        let didSettle = false;
        const settleResolve = () => {
            if (!didSettle) {
                didSettle = true;
                window.clearTimeout(timeoutId);
                resolve();
            }
        };
        const settleReject = (error: Error) => {
            if (!didSettle) {
                didSettle = true;
                window.clearTimeout(timeoutId);
                reject(error);
            }
        };
        const timeoutId = window.setTimeout(() => {
            settleReject(new Error('Timed out loading Google Identity Services'));
        }, 10000);
        const handleLoaded = (script?: HTMLScriptElement | null) => {
            if (script) {
                script.setAttribute(googleScriptStatusAttribute, 'loaded');
            }

            if (window.google?.accounts?.id) {
                settleResolve();
                return;
            }

            settleReject(new Error('Google Identity Services loaded but API is unavailable'));
        };
        const handleError = (script?: HTMLScriptElement | null) => {
            if (script) {
                script.setAttribute(googleScriptStatusAttribute, 'error');
            }

            settleReject(new Error('Failed to load Google Identity Services'));
        };
        const createScript = () => {
            const script = document.createElement('script');
            script.src = googleScriptSrc;
            script.async = true;
            script.defer = true;
            script.onload = () => handleLoaded(script);
            script.onerror = () => handleError(script);
            document.head.appendChild(script);
        };

        const existingScript = document.querySelector<HTMLScriptElement>(googleScriptSelector);
        if (existingScript) {
            const existingScriptStatus = existingScript.getAttribute(googleScriptStatusAttribute);
            if (window.google?.accounts?.id) {
                settleResolve();
                return;
            }

            if (existingScriptStatus === 'error') {
                existingScript.remove();
                createScript();
                return;
            }

            if (existingScriptStatus === 'loaded') {
                handleLoaded(existingScript);
                return;
            }

            existingScript.addEventListener('load', () => handleLoaded(existingScript), { once: true });
            existingScript.addEventListener('error', () => handleError(existingScript), { once: true });
            return;
        }

        createScript();
    }).catch(error => {
        gsiScriptPromise = null;
        throw error;
    });

    gsiScriptPromise = scriptPromise;
    return scriptPromise;
};

export const GoogleSignInButton: FC<GoogleSignInButtonProps> = ({ onSuccess, onError, disabled, clientId }) => {
    const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
    const [buttonWidth, setButtonWidth] = useState(0);
    const locale = resolveGoogleButtonLocale();
    const buttonRef = useRef<HTMLDivElement>(null);
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onSuccessRef.current = onSuccess;
        onErrorRef.current = onError;
    }, [onSuccess, onError]);

    useEffect(() => {
        let isMounted = true;
        ensureGoogleScript()
            .then(() => {
                if (isMounted) {
                    setIsGoogleLoaded(true);
                }
            })
            .catch(error => {
                if (isMounted) {
                    onErrorRef.current?.(error);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const container = buttonRef.current;
        if (!container) {
            return;
        }

        const updateWidth = () => {
            const width = Math.round(container.getBoundingClientRect().width);
            if (width > 0) {
                setButtonWidth(prevWidth => (prevWidth === width ? prevWidth : width));
            }
        };

        updateWidth();

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(() => updateWidth());
            observer.observe(container);
            return () => observer.disconnect();
        }

        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    useEffect(() => {
        if (!isGoogleLoaded || !buttonRef.current || !clientId || !window.google?.accounts?.id) {
            return;
        }

        try {
            const safeWidth = buttonWidth > 0 ? buttonWidth : 320;
            window.google.accounts.id.initialize({
                client_id: clientId,
                itp_support: true,
                callback: (response: any) => {
                    if (response?.credential) {
                        onSuccessRef.current(response.credential);
                        return;
                    }

                    onErrorRef.current?.(new Error('No credential received'));
                },
            });

            buttonRef.current.innerHTML = '';
            window.google.accounts.id.renderButton(buttonRef.current, {
                theme: 'outline',
                size: 'large',
                width: safeWidth,
                text: 'continue_with',
                locale,
            });
        } catch (error) {
            onErrorRef.current?.(error);
        }
    }, [isGoogleLoaded, clientId, buttonWidth, locale]);

    return (
        <div ref={buttonRef} style={{ width: '100%', opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }} />
    );
};
